import { ApolloQueryResult } from 'apollo-client';
import AWSAppSyncClient from 'aws-appsync';
import { loader } from 'graphql.macro';
import React, { useEffect, useState } from 'react';
import {
  GET_PAYMENT_INFO,
  GET_PAYMENT_INFOVariables,
  GET_PAYMENT_INFO_invoices_items,
} from './graphql/queries/__generated__/GET_PAYMENT_INFO';
import { formatDate, formatMoney } from '../../util/formatters';
import useMultipleFeatureFlags from '../../util/hooks/useMultipleFeatureFlags';
import NextPayment, { PaymentInformation } from './NextPayment';
import AgencyBilledQuantity from '../../sharedTypes/AgencyBilledQuantity';
import { LastDayToPayInfoProps } from '../../sharedTypes/Overview';

const getPaymentInfoQuery = loader(
  './graphql/queries/Get_Payment_Info.graphql',
);

const getMigratedStatus = loader(
  './graphql/queries/Has_Migrated_Policy.graphql',
);

interface NextPaymentDataContainerProps {
  accountNumber: string;
  awsAppSyncClient: AWSAppSyncClient<any>;
  sourceSystemCode: string;
  agencyBilledQuantity: AgencyBilledQuantity;
  lastDayToPayInfo: LastDayToPayInfoProps;
}

interface GetMigratedStatusProps {
  hasMigratedPolicy: {
    hasMigratedPolicy: boolean;
  };
}

export interface DisclaimerProps {
  baseState: string | '';
  showDisclaimer: boolean
}

export function getMigratedPolicyData(
  accountNumber: string,
  awsAppSyncClient: AWSAppSyncClient<any>,
): Promise<ApolloQueryResult<GetMigratedStatusProps>> {
  const variables: GET_PAYMENT_INFOVariables = {
    account_number: accountNumber,
  } as GET_PAYMENT_INFOVariables;
  return awsAppSyncClient.query({
    query: getMigratedStatus,
    variables,
  }) as Promise<ApolloQueryResult<GetMigratedStatusProps>>;
}

export function fetchPaymentInfo(
  awsAppSyncClient: AWSAppSyncClient<any>,
  accountNumber: string,
): Promise<ApolloQueryResult<GET_PAYMENT_INFO>> {
  const variables: GET_PAYMENT_INFOVariables = {
    account_number: accountNumber,
  } as GET_PAYMENT_INFOVariables;

  const queryResult: Promise<
    ApolloQueryResult<GET_PAYMENT_INFO>
  > = awsAppSyncClient.query({
    query: getPaymentInfoQuery,
    variables,
  }) as Promise<ApolloQueryResult<GET_PAYMENT_INFO>>;
  return queryResult;
}

export const processShowDisclaimer = (
  flagDetails: any,
  oneIncDisclaimer: string
): DisclaimerProps => {
  let showDisclaimer: boolean = false;
  console.log(oneIncDisclaimer)
  // const mock = {
  //   oneIncDisclaimer: `{
  //       "chargeFee": true,
  //       "baseState": "Connecticut"

  //   }`
  // }
  // const disclaimerDetails = JSON.parse(mock.oneIncDisclaimer);
  const disclaimerDetails = JSON.parse(oneIncDisclaimer);
  const { baseState } = disclaimerDetails;

  if (flagDetails) {
    const PLCCFeeDisclaimerFeatureFlagIndex = flagDetails.findIndex(
      (flag: any) => {
        return flag.flagName === 'PLCCFeeDisclaimer';
      },
    );
    const NoDisclaimerFeeFeatureFlagIndex = flagDetails.findIndex(
      (flag: any) => {
        return flag.flagName === 'DisablePLCCFeeDisclaimerCheck';
      },
    );

    if (flagDetails[NoDisclaimerFeeFeatureFlagIndex]) {
      if (flagDetails[NoDisclaimerFeeFeatureFlagIndex].flagDetails) {
        if (flagDetails[NoDisclaimerFeeFeatureFlagIndex].flagDetails.enabled) {
          showDisclaimer = true;
        }
      }
    }
    if (flagDetails[PLCCFeeDisclaimerFeatureFlagIndex] && !showDisclaimer) {
      if (flagDetails[PLCCFeeDisclaimerFeatureFlagIndex].flagDetails) {
        if (
          flagDetails[PLCCFeeDisclaimerFeatureFlagIndex].flagDetails.enabled
        ) {
          showDisclaimer = disclaimerDetails?.chargeFee;
        }
      }
    }
  }

  return { showDisclaimer, baseState };
};

export const processResponsiveIndicator = (
  flagDetails: any,
  responsiveBillIndicator: string | null | undefined,
) => {
  let responsiveIndicator: string = 'yes';

  const NextPaymentAutoDraftIndicatorFFIndex = flagDetails.findIndex(
    (flag: any) => {
      return flag.flagName === 'NextPaymentAutoDraftIndicator';
    },
  );

  if (flagDetails[NextPaymentAutoDraftIndicatorFFIndex]) {
    if (flagDetails[NextPaymentAutoDraftIndicatorFFIndex].flagDetails) {
      if (
        flagDetails[NextPaymentAutoDraftIndicatorFFIndex].flagDetails.enabled
      ) {
        if (responsiveBillIndicator) {
          responsiveIndicator = responsiveBillIndicator;
        }
      }
    }
  }

  return responsiveIndicator;
};

export const processFeatureFlags = (
  responsiveBillIndicator: string | null | undefined,
  oneIncDisclaimer: any,
  featureFlagDetails: any,
) => {
  let showDisclaimerDecision!: DisclaimerProps;
  // let showDisclaimer: boolean = false;
  let responsiveIndicator: string = 'no';

  if (oneIncDisclaimer !== null && oneIncDisclaimer !== undefined) {
    showDisclaimerDecision = processShowDisclaimer(
      featureFlagDetails,
      oneIncDisclaimer,
    );
  }

  responsiveIndicator = processResponsiveIndicator(
    featureFlagDetails,
    responsiveBillIndicator,
  );
  return {
    responsiveIndicator,
    showDisclaimerDecision,
  };
};

const formatAmountDue = (amountDue: string | null | undefined) => {
  if (!amountDue) {
    return '-';
  }

  if (amountDue.toUpperCase() === 'PAID IN FULL') {
    return 'PAID IN FULL';
  }

  return formatMoney(amountDue);
};

export const showDisclaimerPLModal = (data: GET_PAYMENT_INFO) => {
  let showMigratedModal = false;
  if (
    data?.account?.items &&
    data.account.items[0] &&
    data.account.items[0].policies &&
    data.account.items[0].policies.items
  ) {
    for (
      let i = 0;
      i <= data?.account?.items[0]?.policies?.items?.length - 1;
      i += 1
    ) {
      if (data?.account?.items[0]?.policies?.items[i]?.migrated) {
        if (data.account.items[0]?.policies?.items[i]?.migrated === 'Y') {
          showMigratedModal = true;
        }
      }
    }
  }
  return showMigratedModal;
};

const NextPaymentDataContainer = ({
  accountNumber,
  awsAppSyncClient,
  sourceSystemCode,
  agencyBilledQuantity,
  lastDayToPayInfo,
}: NextPaymentDataContainerProps) => {
  const [paymentInfo, setPaymentInfo] = useState<PaymentInformation>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState<boolean>(true);
  const [showPastDue, setShowPastDue] = useState<boolean>(false);
  const [isMigrated, setIsMigrated] = useState<boolean>(false);
  const [
    onlyMortgageeBilledInvoices,
    setOnlyMortgageeBilledInvoices,
  ] = useState<boolean>(true);
  const [
    mortgageeInvoice,
    setMortgageeInvoice,
  ] = useState<GET_PAYMENT_INFO_invoices_items | null>(null);

  const featureFlagDetails = useMultipleFeatureFlags([
    'NextPaymentAutoDraftIndicator',
    'PLCCFeeDisclaimer',
    'DisablePLCCFeeDisclaimerCheck',
  ]);

  useEffect(() => {
    fetchPaymentInfo(awsAppSyncClient, accountNumber)
      .then((apolloQueryResult: ApolloQueryResult<GET_PAYMENT_INFO>) => {
        if (apolloQueryResult.data.invoices?.items) {
          setMortgageeInvoice(
            apolloQueryResult.data.invoices?.items[0] ?? null,
          );
        }
        if (apolloQueryResult.data.account?.items) {
          const payment =
            apolloQueryResult.data.account?.items[0]
              ?.next_payment_no_mortgagee_billed;
          const processedFeatureFlagIndicators = processFeatureFlags(
            payment?.responsiveBillIndicator,
            apolloQueryResult.data.account?.items[0]?.one_inc_disclaimer,
            featureFlagDetails,
          );

          setPaymentInfo({
            paymentAmount: formatAmountDue(payment?.paymentAmount) ?? '$0.00',
            paymentDueDate: formatDate(payment?.paymentDueDate) ?? '-',
            pastDueAmount: formatMoney(payment?.pastDueAmount) ?? '$0.00',
            defaultBillingMethod: payment?.defaultBillingMethod ?? '-',
            showDisclaimer: processedFeatureFlagIndicators.showDisclaimerDecision,
            responsiveIndicator:
              processedFeatureFlagIndicators.responsiveIndicator,
            userId: apolloQueryResult.data.me?.user_id,
          });

          if (!!payment?.mortgageeBilledOnly === false) {
            setOnlyMortgageeBilledInvoices(false);
          }

          if (
            payment?.pastDueAmount !== '$0.00' &&
            payment?.pastDueAmount !== '0'
          ) {
            setShowPastDue(true);
          }
        }

        setLoading(false);
      })
      .catch((err: Error) => {
        console.error('GET_PAYMENT_INFO ERROR: ', err);
        setError(err);
        setLoading(false);
      });
  }, [
    accountNumber,
    featureFlagDetails[0].flagDetails,
    featureFlagDetails[1].flagDetails,
    featureFlagDetails[2].flagDetails,
    paymentInfo?.paymentDueDate,
  ]);

  useEffect(() => {
    getMigratedPolicyData(accountNumber, awsAppSyncClient)
      .then((apolloQueryResult: ApolloQueryResult<GetMigratedStatusProps>) => {
        if (
          apolloQueryResult.data &&
          apolloQueryResult.data?.hasMigratedPolicy &&
          apolloQueryResult.data?.hasMigratedPolicy?.hasMigratedPolicy
        ) {
          setIsMigrated(
            apolloQueryResult.data.hasMigratedPolicy.hasMigratedPolicy,
          );
        }
      })
      .catch((err: Error) => {
        console.error('GET POLICY MIGRATED STATUS ERROR: ', err);
      });
  }, [accountNumber]);

  return (
    <>
      <NextPayment
        accountNumber={accountNumber}
        paymentInfo={paymentInfo}
        showPastDue={showPastDue}
        awsAppSyncClient={awsAppSyncClient}
        loading={loading}
        error={error}
        onlyMortgageeBilledInvoices={onlyMortgageeBilledInvoices}
        mortgageeInvoice={mortgageeInvoice}
        sourceSystemCode={sourceSystemCode}
        agencyBilledQuantity={agencyBilledQuantity}
        lastDayToPayInfo={lastDayToPayInfo}
        isMigrated={isMigrated}
      />
    </>
  );
};

export default NextPaymentDataContainer;
